import React, { useState, useEffect, useRef } from 'react';
import { formStyles } from '../../styles/formStyles';
import { API_URL } from '../../utils/constants';

function CustomerManagementForm({ token, onUpdate }) {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const searchInputRef = useRef(null);

  const [formData, setFormData] = useState({
    company_name: '',
    manager: '',
    phone: '',
    mobile: '',
    address: ''
  });

  // 고객 검색
  useEffect(() => {
    const performSearch = async () => {
      const onlyNumbers = searchTerm.replace(/[^0-9]/g, '');
      const hasNumbers = onlyNumbers.length >= 4;
      const hasText = searchTerm.length >= 2;

      if (!hasNumbers && !hasText) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/customers/search?term=${encodeURIComponent(searchTerm)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data);
          setShowResults(data.length > 0);
          setSelectedIndex(-1);
        }
      } catch (err) {
        console.error('검색 오류:', err);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, token]);

  // 고객 선택
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      company_name: customer.company_name || '',
      manager: customer.manager || '',
      phone: customer.phone || '',
      mobile: customer.mobile || '',
      address: customer.address || ''
    });
    setSearchTerm('');
    setShowResults(false);
    setSearchResults([]);
  };

  // 키보드 네비게이션
  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => prev < searchResults.length - 1 ? prev + 1 : prev);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0) {
        handleCustomerSelect(searchResults[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowResults(false);
    }
  };

  // F2 단축키
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // 입력 변경
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone' || name === 'mobile') {
      const numValue = value.replace(/[^0-9]/g, '');
      let formatted = numValue;
      if (numValue.length > 3 && numValue.length <= 7) {
        formatted = numValue.replace(/(\d{3})(\d{1,4})/, '$1-$2');
      } else if (numValue.length > 7) {
        formatted = numValue.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
      }
      setFormData(prev => ({ ...prev, [name]: formatted }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 수정 또는 신규 등록
  const handleSave = async () => {
    if (!formData.company_name) {
      alert('업체명은 필수입니다.');
      return;
    }

    try {
      if (selectedCustomer) {
        // 수정
        const response = await fetch(`${API_URL}/customers/${selectedCustomer.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            companyName: formData.company_name,
            manager: formData.manager,
            phone: formData.phone,
            mobile: formData.mobile,
            address: formData.address
          })
        });

        if (response.ok) {
          alert('수정되었습니다.');
          handleReset();
          if (onUpdate) onUpdate();
        } else {
          alert('수정 실패');
        }
      } else {
        // 신규 등록
        const response = await fetch(`${API_URL}/customers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            companyName: formData.company_name,
            manager: formData.manager,
            phone: formData.phone,
            mobile: formData.mobile,
            address: formData.address
          })
        });

        if (response.ok) {
          alert('등록되었습니다.');
          handleReset();
          if (onUpdate) onUpdate();
        } else {
          alert('등록 실패');
        }
      }
    } catch (err) {
      alert('저장 중 오류 발생');
    }
  };

  // 삭제
  const handleDelete = async () => {
    if (!selectedCustomer) {
      alert('삭제할 고객을 선택하세요.');
      return;
    }

    if (!window.confirm('이 고객 정보를 삭제하시겠습니까?\n연결된 작업 데이터는 삭제되지 않습니다.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/customers/${selectedCustomer.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('삭제되었습니다.');
        handleReset();
      } else {
        alert('삭제 실패');
      }
    } catch (err) {
      alert('삭제 중 오류 발생');
    }
  };

  // 초기화
  const handleReset = () => {
    setSelectedCustomer(null);
    setFormData({
      company_name: '',
      manager: '',
      phone: '',
      mobile: '',
      address: ''
    });
    setSearchTerm('');
  };

  return (
    <aside style={formStyles.formContainer}>
      {/* 고객 검색 */}
      <div style={formStyles.formSectionTitle}>고객 검색 (F2)</div>
      <div style={{ position: 'relative', marginBottom: '20px' }}>
        <input 
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="업체명/담당자 (2글자) 또는 전화번호 (4자리)"
          style={{
            ...formStyles.input,
            width: '100%',
            padding: '12px',
            fontSize: '15px',
            border: '2px solid #3498db'
          }}
        />
        
        {showResults && searchResults.length > 0 && (
          <div style={formStyles.searchResultsDropdown}>
            {searchResults.map((customer, index) => (
              <div
                key={customer.id}
                onClick={() => handleCustomerSelect(customer)}
                onMouseEnter={() => setSelectedIndex(index)}
                style={{
                  ...formStyles.searchResultItem,
                  backgroundColor: selectedIndex === index ? '#e3f2fd' : 'white'
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                  {customer.company_name}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {customer.manager && `담당: ${customer.manager}`}
                  {customer.manager && (customer.phone || customer.mobile) && ' | '}
                  {customer.phone && `☎ ${customer.phone}`}
                  {customer.phone && customer.mobile && ' / '}
                  {customer.mobile && `📱 ${customer.mobile}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 고객 정보 */}
      <div style={formStyles.formSectionTitle}>고객 정보</div>
      
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <div style={{ flex: 1 }}>
          <label style={formStyles.label}>업체명 *</label>
          <input 
            name="company_name" 
            style={formStyles.input} 
            value={formData.company_name} 
            onChange={handleInputChange} 
            placeholder="업체명"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={formStyles.label}>담당자</label>
          <input 
            name="manager" 
            style={formStyles.input} 
            value={formData.manager} 
            onChange={handleInputChange} 
            placeholder="담당자명"
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <div style={{ flex: 1 }}>
          <label style={formStyles.label}>전화번호</label>
          <input 
            name="phone" 
            style={formStyles.input} 
            value={formData.phone} 
            onChange={handleInputChange}
            placeholder="전화번호"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={formStyles.label}>휴대폰번호</label>
          <input 
            name="mobile" 
            style={formStyles.input} 
            value={formData.mobile} 
            onChange={handleInputChange}
            placeholder="휴대폰번호"
          />
        </div>
      </div>

      <div style={formStyles.inputGroup}>
        <label style={formStyles.label}>주소</label>
        <input 
          name="address" 
          style={formStyles.input} 
          value={formData.address} 
          onChange={handleInputChange}
          placeholder="주소"
        />
      </div>

      {/* 버튼 */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button 
          onClick={handleSave}
          style={{ 
            ...formStyles.submitButton, 
            backgroundColor: '#3498db',
            flex: 1
          }}
        >
          저장
        </button>
        <button 
          onClick={handleDelete}
          disabled={!selectedCustomer}
          style={{ 
            ...formStyles.submitButton, 
            backgroundColor: selectedCustomer ? '#e74c3c' : '#95a5a6',
            flex: 1
          }}
        >
          삭제
        </button>
      </div>
      <button 
        onClick={handleReset}
        style={{ 
          ...formStyles.submitButton, 
          backgroundColor: '#95a5a6',
          marginTop: '10px'
        }}
      >
        초기화
      </button>
    </aside>
  );
}

export default CustomerManagementForm;
